/**
 * The identifier in a conversation's URL: `c<id>`.
 *
 * The `c` prefix rather than a bare number keeps it visibly distinct from a provider
 * thread id — Gmail thread ids are hex, and a short all-digit one like `18632` is a
 * perfectly good Gmail id *and* a plausible conversation id.
 *
 * The check is anchored and strict: `c` followed by digits and nothing else.
 */

const INTERNAL_REF = /^c(\d+)$/;

/** The URL segment for a conversation. */
export function conversationRef(conversationId: number): string {
  return `c${conversationId}`;
}

/**
 * Reads a route parameter as a conversation id, or null when it is not one.
 *
 * Never throws: a malformed ref is a 404 on an unknown conversation, not a crash
 * mid-render on a link someone mistyped.
 */
export function parseConversationRef(ref: string): number | null {
  const match = INTERNAL_REF.exec(ref);
  if (!match) return null;
  const conversationId = Number.parseInt(match[1], 10);
  // A ref that parses but overflows is not an id anybody issued.
  return Number.isSafeInteger(conversationId) && conversationId > 0 ? conversationId : null;
}

/**
 * The ref to put in a link for a thread row, or null when the row has no conversation.
 *
 * Ingest writes the thread row and then its conversation in one round, and replays the
 * round if the second write fails, so a null here is a brief window rather than a state.
 */
export function threadLinkRef(thread: { conversationId?: number | null }): string | null {
  return thread.conversationId ? conversationRef(thread.conversationId) : null;
}
